import React, { createContext, useContext, useState, useCallback } from 'react';

// =============================================================================
// КОНТЕКСТ ВАЛИДАЦИИ
// =============================================================================
const ValidationContext = createContext(null);

// =============================================================================
// ПРОВАЙДЕР
// =============================================================================
export const ValidationProvider = ({ children }) => {
    const [showValidation, setShowValidation] = useState(false);

    /**
     * Проверяет, заполнено ли значение
     */
    const isFieldFilled = useCallback((value) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim() !== '';
        if (typeof value === 'number') return true;
        if (typeof value === 'boolean') return true;
        return false;
    }, []);

    /**
     * Валидация всех обязательных полей в контракте
     * Паспортные данные НЕ обязательны
     */
    const validateContract = useCallback((contractData) => {
        const errors = [];

        // 1. Шапка и стороны
        if (!isFieldFilled(contractData.header?.city)) {
            errors.push({ section: 'header', field: 'city', message: 'Не указан город' });
        }
        if (!isFieldFilled(contractData.parties?.landlordName)) {
            errors.push({ section: 'parties', field: 'landlordName', message: 'Не указано имя наймодателя' });
        }
        if (!isFieldFilled(contractData.parties?.tenantName)) {
            errors.push({ section: 'parties', field: 'tenantName', message: 'Не указано имя нанимателя' });
        }

        // 2. Предмет договора (недвижимость)
        if (!isFieldFilled(contractData.property?.roomCount)) {
            errors.push({ section: 'property', field: 'roomCount', message: 'Не указано количество комнат' });
        }
        if (!isFieldFilled(contractData.property?.totalArea)) {
            errors.push({ section: 'property', field: 'totalArea', message: 'Не указана общая площадь' });
        }
        if (!isFieldFilled(contractData.property?.address)) {
            errors.push({ section: 'property', field: 'address', message: 'Не указан адрес' });
        }
        if (!isFieldFilled(contractData.property?.buildingFloors)) {
            errors.push({ section: 'property', field: 'buildingFloors', message: 'Не указана этажность здания' });
        }
        if (!isFieldFilled(contractData.property?.apartmentFloor)) {
            errors.push({ section: 'property', field: 'apartmentFloor', message: 'Не указан этаж квартиры' });
        }

        // 2.1. Подтверждение права собственности (если выбрана выписка из ЕГРН или документ)
        const ownershipConfirmation = contractData.property?.ownershipConfirmation || {};
        if (ownershipConfirmation.type === 'egrn' && !isFieldFilled(ownershipConfirmation.details)) {
            errors.push({ section: 'property', field: 'egrnExtract', message: 'Не указан номер и дата выписки из ЕГРН' });
        }
        if (ownershipConfirmation.type === 'document' && !isFieldFilled(ownershipConfirmation.details)) {
            errors.push({ section: 'property', field: 'ownershipDocument', message: 'Не указан документ о праве собственности' });
        }

        // 3. Оплата
        if (!isFieldFilled(contractData.payment?.monthlyAmount?.amount)) {
            errors.push({ section: 'payment', field: 'monthlyAmount', message: 'Не указана сумма ежемесячной оплаты' });
        }

        // 4. Подписи (контактные данные обязательны, паспортные - нет)
        if (!isFieldFilled(contractData.signatures?.landlord?.fullName)) {
            errors.push({ section: 'signatures', field: 'landlordFullName', message: 'Не указано полное имя наймодателя' });
        }
        if (!isFieldFilled(contractData.signatures?.landlord?.address)) {
            errors.push({ section: 'signatures', field: 'landlordAddress', message: 'Не указан адрес наймодателя' });
        }
        if (!isFieldFilled(contractData.signatures?.landlord?.phone)) {
            errors.push({ section: 'signatures', field: 'landlordPhone', message: 'Не указан телефон наймодателя' });
        }
        
        if (!isFieldFilled(contractData.signatures?.tenant?.fullName)) {
            errors.push({ section: 'signatures', field: 'tenantFullName', message: 'Не указано полное имя нанимателя' });
        }
        if (!isFieldFilled(contractData.signatures?.tenant?.address)) {
            errors.push({ section: 'signatures', field: 'tenantAddress', message: 'Не указан адрес нанимателя' });
        }
        if (!isFieldFilled(contractData.signatures?.tenant?.phone)) {
            errors.push({ section: 'signatures', field: 'tenantPhone', message: 'Не указан телефон нанимателя' });
        }

        // 5. Валидация акта приема-передачи (если включен)
        if (contractData.attachments?.handoverAct?.enabled) {
            const handoverItems = contractData.property?.handoverItems || {};
            
            // Валидация шапки акта
            if (!isFieldFilled(contractData.header?.number)) {
                errors.push({ section: 'handoverAct', field: 'headerNumber', message: 'Не указан номер договора в акте' });
            }
            if (!isFieldFilled(contractData.header?.date)) {
                errors.push({ section: 'handoverAct', field: 'headerDate', message: 'Не указана дата договора в акте' });
            }
            if (!isFieldFilled(contractData.header?.city)) {
                errors.push({ section: 'handoverAct', field: 'headerCity', message: 'Не указан город в акте' });
            }

            // Валидация результата приемки
            const acceptance = handoverItems.acceptance || {};
            if (!acceptance.suitableForLiving && acceptance.deficiencies) {
                if (!isFieldFilled(acceptance.deficiencies?.list)) {
                    errors.push({ section: 'handoverAct', field: 'deficienciesList', message: 'Не указан перечень недостатков' });
                }
                if (acceptance.defectsRepairDeadline && !isFieldFilled(acceptance.defectsRepairDeadline?.days)) {
                    errors.push({ section: 'handoverAct', field: 'defectsRepairDeadline', message: 'Не указаны сроки устранения недостатков' });
                }
            }

            // Валидация состояния помещения (если раздел включен)
            const propertyCondition = handoverItems.propertyCondition || {};
            if (propertyCondition.enabled) {
                if (propertyCondition.apartmentEnabled && !isFieldFilled(propertyCondition.apartment)) {
                    errors.push({ section: 'handoverAct', field: 'apartmentCondition', message: 'Не указано состояние квартиры' });
                }
                if (propertyCondition.equipmentEnabled && !isFieldFilled(propertyCondition.equipment)) {
                    errors.push({ section: 'handoverAct', field: 'equipmentCondition', message: 'Не указано состояние оборудования' });
                }
                if (propertyCondition.appliancesEnabled && !isFieldFilled(propertyCondition.appliances)) {
                    errors.push({ section: 'handoverAct', field: 'appliancesCondition', message: 'Не указано состояние бытовой техники' });
                }
                if (propertyCondition.furnitureEnabled && !isFieldFilled(propertyCondition.furniture)) {
                    errors.push({ section: 'handoverAct', field: 'furnitureCondition', message: 'Не указано состояние мебели' });
                }
            }

            // Валидация ключей и документации (если раздел включен)
            const keysAndDocuments = handoverItems.keysAndDocuments || {};
            if (keysAndDocuments.enabled) {
                if (!isFieldFilled(keysAndDocuments.keys?.count)) {
                    errors.push({ section: 'handoverAct', field: 'keysCount', message: 'Не указано количество ключей' });
                }
                if (keysAndDocuments.documentation && !isFieldFilled(keysAndDocuments.documentation?.description)) {
                    errors.push({ section: 'handoverAct', field: 'documentation', message: 'Не указана документация' });
                }
            }

            // Валидация показаний приборов учета (если раздел включен)
            const meterReadings = handoverItems.meterReadings || {};
            if (meterReadings.enabled) {
                if (meterReadings.electricity && (!isFieldFilled(meterReadings.electricity.readings) || !isFieldFilled(meterReadings.electricity.number))) {
                    errors.push({ section: 'handoverAct', field: 'electricityMeter', message: 'Не полностью заполнены показания счетчика электроэнергии' });
                }
                if (meterReadings.hotWater && (!isFieldFilled(meterReadings.hotWater.readings) || !isFieldFilled(meterReadings.hotWater.number))) {
                    errors.push({ section: 'handoverAct', field: 'hotWaterMeter', message: 'Не полностью заполнены показания счетчика горячей воды' });
                }
                if (meterReadings.coldWater && (!isFieldFilled(meterReadings.coldWater.readings) || !isFieldFilled(meterReadings.coldWater.number))) {
                    errors.push({ section: 'handoverAct', field: 'coldWaterMeter', message: 'Не полностью заполнены показания счетчика холодной воды' });
                }
                if (meterReadings.gas && (!isFieldFilled(meterReadings.gas.readings) || !isFieldFilled(meterReadings.gas.number))) {
                    errors.push({ section: 'handoverAct', field: 'gasMeter', message: 'Не полностью заполнены показания счетчика газа' });
                }
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }, [isFieldFilled]);

    /**
     * Запускает валидацию и устанавливает флаг showValidation
     */
    const triggerValidation = useCallback((contractData) => {
        const result = validateContract(contractData);
        setShowValidation(!result.isValid);

        let firstInvalidFieldId = null;
        if (!result.isValid && result.errors.length > 0) {
            const firstError = result.errors[0];
            firstInvalidFieldId = `${firstError.section}-${firstError.field}`;
        }

        return { ...result, firstInvalidFieldId };
    }, [validateContract]);

    /**
     * Сброс состояния валидации
     */
    const resetValidation = useCallback(() => {
        setShowValidation(false);
    }, []);

    /**
     * Прокрутка к первому невалидному полю.
     * @param {string|null|undefined} explicitFieldId — `${section}-${field}` из результата triggerValidation (предпочтительно, порядок как в валидации).
     * @param {() => void} [onDone] — вызвать после прокрутки (например открыть модалку; иначе body с position:fixed сорвёт scrollIntoView).
     */
    const scrollToFirstInvalidField = useCallback((explicitFieldId, onDone) => {
        const runScroll = () => {
            const fieldId = explicitFieldId;
            let el = fieldId
                ? document.querySelector(`[data-validation-field="${fieldId}"]`)
                : null;

            // Поля без data-validation-field: берём любое с красной подсветкой (порядок DOM ≠ порядок ошибок)
            if (!el) {
                const errorFields = document.querySelectorAll(
                    '.inline-input.is-validation-error, .inline-textarea.is-validation-error'
                );
                el = errorFields[0] || null;
            }

            if (el) {
                // instant — до открытия модалки с lock scroll, иначе визуально остаёмся в начале страницы
                el.scrollIntoView({
                    behavior: 'auto',
                    block: 'center',
                    inline: 'nearest'
                });
            }

            const finish = () => {
                if (typeof onDone === 'function') {
                    onDone();
                } else if (el) {
                    try {
                        el.focus({ preventScroll: true });
                    } catch {
                        el.focus();
                    }
                }
            };

            // Дать браузеру применить scroll, затем колбэк (открытие модалки)
            requestAnimationFrame(() => {
                requestAnimationFrame(finish);
            });
        };

        // Два кадра: дождаться коммита React и отрисовки классов/атрибутов
        requestAnimationFrame(() => {
            requestAnimationFrame(runScroll);
        });
    }, []);

    const contextValue = {
        showValidation,
        validateContract,
        triggerValidation,
        resetValidation,
        scrollToFirstInvalidField,
        isFieldFilled
    };

    return (
        <ValidationContext.Provider value={contextValue}>
            {children}
        </ValidationContext.Provider>
    );
};

// =============================================================================
// ХУК
// =============================================================================
export const useValidation = () => {
    const context = useContext(ValidationContext);
    if (!context) {
        throw new Error('useValidation должен использоваться внутри ValidationProvider');
    }
    return context;
};

export default ValidationContext;

